// src/pages/GalleriesPhotos/GalleriesPhotos.jsx
import React, { useState, useEffect } from "react";
import { getAllGalleries } from "./../../services/gallerieServices";
import { useUser } from "./../../contexts/UserContext";
import { getRoleFromRoleId } from "./../../utils/roleHelper";
import useScrollToTop from "./../../hooks/useScrollToTop";
import Button from "./../../components/Button/Button";
import GalleryCard from "./GalleryCard";
import { ToastContainer, toast } from "react-toastify";
import InfoBubble from "./../../components/InfoBubble/InfoBubble";
import CreateAlbumModal from "./CreateAlbumModal";
import ScrollIndicator from "./../../hooks/ScrollIndicator";
import { FaEye, FaEyeSlash, FaPlus } from "react-icons/fa";
import PageHeader from "../../components/Layout/PageHeader";

const GalleriesPhotos = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [galleries, setGalleries] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const isAdmin = userRole === "Administrateur" || userRole === "Bureau";

  const fetchGalleries = async () => {
    try {
      const response = await getAllGalleries();
      const filteredGalleries = showAll
        ? response
        : response.filter((gallery) => gallery.is_active);
      setGalleries(filteredGalleries);
    } catch (error) {
      console.error("Failed to fetch galleries:", error);
      toast.error("Erreur lors du chargement des galeries.");
    }
  };

  useEffect(() => {
    fetchGalleries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAll]);

  const handleAddGallery = () => {
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    fetchGalleries();
  };

  const toggleShowAll = () => {
    setShowAll((prev) => !prev);
  };

  return (
    <div className="min-h-screen flex flex-col w-full mx-auto pt-[130px] sm:pt-[130px] md:pt-[150px] lg:pt-[150px] xl:pt-[150px] pb-10 bg-slate-50 dark:bg-slate-900 dark:text-white">
      <ScrollIndicator />

      {/* Header */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <PageHeader
          eyebrow="Visiteurs & Utilisateurs"
          title="Galeries Photos"
          subtitle="Retrouvez ici les albums photos : compétitions, animations, stages, podiums et temps forts du comité départemental."
          align="center"
          size="lg"
          className="mb-6 md:mb-8"
        />

        <InfoBubble title="Bienvenue sur la page Galeries Photos">
          <p className="text-sm">
            Parcourez les albums publics. Les administrateurs peuvent créer de
            nouveaux albums, les éditer ou afficher les albums archivés.
          </p>
        </InfoBubble>
      </div>

      {/* Barre d’actions */}
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="rounded-2xl bg-white/95 dark:bg-slate-900/95 border border-slate-200/80 dark:border-slate-700/80 shadow-lg">
          <div className="h-1.5 bg-gradient-to-r from-primary via-indigo-500 to-cyan-400" />
          <div className="p-4 md:p-5 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="text-sm text-slate-600 dark:text-slate-300">
              <span className="font-semibold">
                {galleries.length} album
                {galleries.length > 1 && "s"}
              </span>{" "}
              actuellement affiché
              {galleries.length > 1 && "s"}.
            </div>

            {isAdmin && (
              <div className="flex flex-wrap gap-3 justify-start md:justify-end">
                <Button
                  text="Ajouter un Album"
                  onClick={handleAddGallery}
                  icon={FaPlus}
                  className="gap-2 text-sm"
                />
                <Button
                  text={
                    showAll ? "Voir uniquement actifs" : "Voir tous les albums"
                  }
                  onClick={toggleShowAll}
                  icon={showAll ? FaEyeSlash : FaEye}
                  className="gap-2 text-sm btn-ghost"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Liste des galeries */}
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        {galleries.length === 0 ? (
          <div className="text-center text-sm text-slate-500 dark:text-slate-300 mt-8">
            Aucune galerie photo disponible pour le moment.
          </div>
        ) : (
          <div className="space-y-6">
            {galleries.map((gallery, index) => (
              <GalleryCard
                key={gallery.id}
                gallery={gallery}
                index={index}
                userRole={userRole}
                refreshGalleriesList={fetchGalleries}
              />
            ))}
          </div>
        )}
      </div>

      <ToastContainer />

      {isCreateModalOpen && (
        <CreateAlbumModal
          isOpen={isCreateModalOpen}
          onClose={closeCreateModal}
          refreshList={fetchGalleries}
        />
      )}
    </div>
  );
};

export default GalleriesPhotos;
